import { ConnectionType } from '@sembo-private/maps';

import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type {
  FlightIngredient,
  FerryIngredient,
  HotelIngredient,
  RecipeMainIngredient,
} from '@apps/main/modules/recipe/ingredients';
import {
  isFlightIngredient,
  isHotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import type { StayIngredient } from '@apps/main/modules/recipe/ingredients/typeGuards';
import {
  isOwnStayIngredient,
  isStayIngredient,
  isTransportIngredient,
} from '@apps/main/modules/recipe/ingredients/typeGuards';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import {
  getActiveFilters,
  getHotelFilterFromIngredient,
} from '@apps/main/new-desktop/modules/hotelFilters';
import { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';

import type { CarrierName, IngredientId } from '@libs/trip-planner-interfaces';
import { AvailabilityOrder } from '@libs/trip-planner-interfaces';
import { isStena } from '@libs/cookie';

import { ItinerarySegmentType, ProductCardTopBarType } from '../models';
import type {
  ItineraryLoaderSegment,
  ItineraryStenaUpsellNoTransportSegment,
  ItineraryUpsellNoTransportSegment,
  LocationSegment,
} from '../models';

import {
  getCityText,
  getCountryText,
  getLocationSegment,
} from './tripOverviewSegments/tripOverviewLocationSegmentsLogic';
import {
  isSimpleTransport,
  getItinerarySegmentTypeFromConnectionType,
} from './tripOverviewSegments/tripOverviewSimpleTransportSegmentsLogic';
import {
  getFerryFilterPills,
  getFlightFilterPills,
} from './tripOverviewSegments/tripOverviewTransportFiltersPillsLogic';

interface GetHotelLoaderSegmentsParams {
  recipe: RecipeMainIngredient[];
  ingredient: HotelIngredient;
  ingredientIndex: number;
  isStena: boolean;
  isDesktop: boolean;
  getAllCarriers: (ingredientId: IngredientId) => CarrierName[];
}

const { t } = i18n.global;

/* eslint-disable @typescript-eslint/no-unused-vars */

export function getTripItineraryLoaderSegments(
  recipe: RecipeMainIngredient[],
  endLocationIngredient: HotelIngredient | undefined,
  isGeolocationAvailable: boolean,
  isDesktop: boolean,
  getAllCarriers: (ingredientId: IngredientId) => CarrierName[]
): ItineraryLoaderSegment[] {
  const loaderSegments: ItineraryLoaderSegment[] = [
    { segmentType: ItinerarySegmentType.DateRange },
  ];

  const firstIngredient = recipe[0];

  if (
    isGeolocationAvailable &&
    firstIngredient &&
    isStayIngredient(firstIngredient) &&
    firstIngredient.transportTypeToBeReached !== ConnectionType.NONE
  ) {
    loaderSegments.push({
      segmentType: ItinerarySegmentType.StartLocation,
      location: { name: t('planner.your_position') },
    });
  }

  const recipeLoaders = recipe.flatMap(
    (ingredient: RecipeMainIngredient, index: number) => {
      const ingredientLoaders: ItineraryLoaderSegment[] = [];

      if (isHotelIngredient(ingredient)) {
        const hotelLoadersSementParams: GetHotelLoaderSegmentsParams = {
          recipe,
          ingredient,
          ingredientIndex: index,
          isStena: isStena(),
          isDesktop,
          getAllCarriers,
        };

        const hotelLoaders = getHotelLoaderSegments(hotelLoadersSementParams);
        ingredientLoaders.push(...hotelLoaders);
      }

      if (isOwnStayIngredient(ingredient)) {
        const ownStayLoaders = getOwnStayLoaderSegments(
          recipe,
          ingredient,
          index,
          getAllCarriers
        );
        ingredientLoaders.push(...ownStayLoaders);
      }

      const isLastIngredientOnlyBookableTransport =
        isTransportIngredient(ingredient) && index === recipe.length - 1;

      if (isLastIngredientOnlyBookableTransport) {
        ingredientLoaders.push({
          segmentType: ItinerarySegmentType.DateRange,
        });

        const productCardTopBarType = getTransportProductTopBarType(ingredient);

        let suggestionType: SuggestionType | undefined = undefined;
        if (productCardTopBarType !== ProductCardTopBarType.Selected) {
          suggestionType =
            productCardTopBarType === ProductCardTopBarType.Suggestion
              ? SuggestionType.MostPopular
              : SuggestionType.Cheapest;
        }

        if (isFlightIngredient(ingredient)) {
          const filtersPills = getFlightFilterPills(
            ingredient.filters,
            getAllCarriers(ingredient.ingredientId)
          );

          ingredientLoaders.push({
            segmentType: ItinerarySegmentType.Flight,
            ingredientId: ingredient.ingredientId,
            isLastSegment: true,
            barType: productCardTopBarType,
            filtersPills,
            suggestionType,
          });
        } else {
          const filtersPills = getFerryFilterPills(ingredient.filters);

          ingredientLoaders.push({
            segmentType: ItinerarySegmentType.Ferry,
            ingredientId: ingredient.ingredientId,
            isLastSegment: true,
            barType: productCardTopBarType,
            filtersPills,
            suggestionType,
          });
        }
      }

      return ingredientLoaders;
    }
  );

  loaderSegments.push(...recipeLoaders);

  const lastRecipeIngredient = recipe[recipe.length - 1];

  if (
    lastRecipeIngredient &&
    isStayIngredient(lastRecipeIngredient) &&
    endLocationIngredient
  ) {
    if (isSimpleTransport(endLocationIngredient.transportTypeToBeReached)) {
      loaderSegments.push({
        segmentType: getItinerarySegmentTypeFromConnectionType(
          endLocationIngredient.transportTypeToBeReached
        ),
        ingredientId: lastRecipeIngredient.ingredientId,
        isLastSegment: true,
      });
    } else {
      loaderSegments.push({
        segmentType: ItinerarySegmentType.NoTransport,
        ingredientId: lastRecipeIngredient.ingredientId,
        isReturnTransport: true,
        isLastSegment: true,
      });
    }
  }

  if (
    lastRecipeIngredient &&
    isStayIngredient(lastRecipeIngredient) &&
    endLocationIngredient &&
    endLocationIngredient.transportTypeToBeReached !== ConnectionType.NONE
  ) {
    loaderSegments.push({
      segmentType: ItinerarySegmentType.EndLocation,
      location: { name: t('planner.your_position') },
    });
  }

  return loaderSegments;
}

function getTransportProductTopBarType(
  ingerdient: FlightIngredient | FerryIngredient
): ProductCardTopBarType | undefined {
  if (ingerdient.transportIds?.length) {
    return undefined;
  }

  if (ingerdient.availabilityOrder === AvailabilityOrder.Popular) {
    return ProductCardTopBarType.Suggestion;
  } else {
    return ProductCardTopBarType.Cheapest;
  }
}

function getHotelProductTopBarType(
  ingerdient: HotelIngredient
): ProductCardTopBarType {
  if (ingerdient.heartedHotels?.length) {
    return ProductCardTopBarType.Selected;
  }

  return ingerdient.availabilityOrder === AvailabilityOrder.Popular
    ? ProductCardTopBarType.Suggestion
    : ProductCardTopBarType.Cheapest;
}

function getHotelLoaderSegments(
  params: GetHotelLoaderSegmentsParams
): ItineraryLoaderSegment[] {
  const ingredientLoaders: ItineraryLoaderSegment[] = [];

  const previousIngredient: RecipeMainIngredient | undefined =
    params.recipe[params.ingredientIndex - 1];

  ingredientLoaders.push(
    ...getBookableTransportLoaderSegments(
      params.ingredientIndex,
      previousIngredient,
      params.getAllCarriers
    )
  );

  if (
    params.ingredientIndex === 0 &&
    !isSimpleTransport(params.ingredient.transportTypeToBeReached)
  ) {
    if (params.isStena) {
      ingredientLoaders.push(getStenaUpsellLoaderSegments(params.ingredient));
    } else {
      if (params.isDesktop) {
        ingredientLoaders.push(
          getTransportUpsellLoaderSegments(params.ingredient)
        );
      } else {
        ingredientLoaders.push(
          ...getSimpleTransportLoaderSegments(
            params.ingredient,
            previousIngredient
          )
        );
      }
    }
  } else {
    ingredientLoaders.push(
      ...getSimpleTransportLoaderSegments(params.ingredient, previousIngredient)
    );
  }

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.MiddleLocation,
    location: getHotelMiddleLocationLoaderLocationSegment(params.ingredient),
    numberOfNights: params.ingredient.numberOfNights,
    ingredientId: params.ingredient.ingredientId,
  });

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.Accommodation,
    barType: getHotelProductTopBarType(params.ingredient),
    ingredientId: params.ingredient.ingredientId,
    ingredientIndex: params.ingredientIndex,
    filters: params.ingredient.filters
      ? getActiveFilters(
          getHotelFilterFromIngredient(params.ingredient.filters)
        )
      : [],
  });

  params.ingredient.subIngredients.forEach((subIngredient) =>
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Activity,
      barType: undefined,
      ingredientId: subIngredient.ingredientId,
      ingredientIndex: params.ingredientIndex,
    })
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.Upsell,
    ingredientId: params.ingredient.ingredientId,
  });

  return ingredientLoaders;
}

function getOwnStayLoaderSegments(
  recipe: RecipeMainIngredient[],
  ingredient: OwnStayIngredient,
  ingredientIndex: number,
  getAllCarriers: (ingredientId: IngredientId) => CarrierName[]
): ItineraryLoaderSegment[] {
  const ingredientLoaders: ItineraryLoaderSegment[] = [];

  const previousIngredient: RecipeMainIngredient | undefined =
    recipe[ingredientIndex - 1];

  ingredientLoaders.push(
    ...getBookableTransportLoaderSegments(
      ingredientIndex,
      previousIngredient,
      getAllCarriers
    )
  );

  ingredientLoaders.push(
    ...getSimpleTransportLoaderSegments(ingredient, previousIngredient)
  );

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.MiddleLocation,
    location: getLocationSegment(ingredient.displayText),
    numberOfNights: ingredient.numberOfNights,
    ingredientId: ingredient.ingredientId,
  });

  ingredientLoaders.push({
    segmentType: ItinerarySegmentType.OwnStay,
    addAccommodationMessage: `${t(
      'planner.no_availability.actions.explore_hotels_selection_in'
    )} ${getCityText(ingredient.displayText)}`,
    context: {
      ingredientId: ingredient.ingredientId,
      ingredientIndex,
    },
  });

  ingredient.subIngredients.forEach((subIngredient) =>
    ingredientLoaders.push({
      segmentType: ItinerarySegmentType.Activity,
      barType: undefined,
      ingredientId: subIngredient.ingredientId,
      ingredientIndex,
    })
  );

  return ingredientLoaders;
}

function getBookableTransportLoaderSegments(
  index: number,
  previousIngredient: RecipeMainIngredient | undefined,
  getAllCarriers: (ingredientId: IngredientId) => CarrierName[]
): ItineraryLoaderSegment[] {
  const bookableTransportLoaderSegments: ItineraryLoaderSegment[] = [];

  const isPreviousIngredientBookableTransport =
    previousIngredient && isTransportIngredient(previousIngredient);

  if (index !== 1 && isPreviousIngredientBookableTransport) {
    bookableTransportLoaderSegments.push({
      segmentType: ItinerarySegmentType.DateRange,
    });
  }

  if (isPreviousIngredientBookableTransport) {
    const productCardTopBarType =
      getTransportProductTopBarType(previousIngredient);

    let suggestionType: SuggestionType | undefined = undefined;
    if (productCardTopBarType !== ProductCardTopBarType.Selected) {
      suggestionType =
        productCardTopBarType === ProductCardTopBarType.Suggestion
          ? SuggestionType.MostPopular
          : SuggestionType.Cheapest;
    }

    if (isFlightIngredient(previousIngredient)) {
      const filtersPills = getFlightFilterPills(
        previousIngredient.filters,
        getAllCarriers(previousIngredient.ingredientId)
      );

      bookableTransportLoaderSegments.push({
        segmentType: ItinerarySegmentType.Flight,
        ingredientId: previousIngredient.ingredientId,
        barType: productCardTopBarType,
        filtersPills,
        suggestionType,
      });
    } else {
      const filtersPills = getFerryFilterPills(previousIngredient.filters);

      bookableTransportLoaderSegments.push({
        segmentType: ItinerarySegmentType.Ferry,
        ingredientId: previousIngredient.ingredientId,
        barType: productCardTopBarType,
        filtersPills,
        suggestionType,
      });
    }
  }

  return bookableTransportLoaderSegments;
}

function getSimpleTransportLoaderSegments(
  ingredient: StayIngredient,
  previousIngredient: RecipeMainIngredient | undefined
): ItineraryLoaderSegment[] {
  const simpleTransportLoaderSegments: ItineraryLoaderSegment[] = [];

  const isPreviousIngredientStay =
    previousIngredient && isStayIngredient(previousIngredient);

  if (isPreviousIngredientStay) {
    simpleTransportLoaderSegments.push({
      segmentType: ItinerarySegmentType.DateRange,
    });
  }

  if (isPreviousIngredientStay || !previousIngredient) {
    if (isSimpleTransport(ingredient.transportTypeToBeReached)) {
      simpleTransportLoaderSegments.push({
        segmentType: getItinerarySegmentTypeFromConnectionType(
          ingredient.transportTypeToBeReached
        ),
        ingredientId: ingredient.ingredientId,
      });
    } else {
      simpleTransportLoaderSegments.push({
        segmentType: ItinerarySegmentType.NoTransport,
        ingredientId: ingredient.ingredientId,
        isReturnTransport: false,
      });
    }
  }

  return simpleTransportLoaderSegments;
}

function getHotelMiddleLocationLoaderLocationSegment(
  ingredient: HotelIngredient
): LocationSegment {
  const polygonText =
    ingredient.commonPolygonText ||
    ingredient.displayText ||
    t('planner.your_destination');

  return getLocationSegment(polygonText);
}

function getTransportUpsellLoaderSegments(
  ingredient: HotelIngredient
): ItineraryUpsellNoTransportSegment {
  return {
    segmentType: ItinerarySegmentType.UpsellNoTransport,
    ingredientId: ingredient.ingredientId,
    destination: getCountryText(ingredient.displayText, ingredient.searchText),
  };
}

function getStenaUpsellLoaderSegments(
  ingredient: HotelIngredient
): ItineraryStenaUpsellNoTransportSegment {
  return {
    segmentType: ItinerarySegmentType.StenaUpsellNoTransport,
    ingredientId: ingredient.ingredientId,
  };
}
